import { useState } from 'react';
import { Alert, Box, Chip, Snackbar, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { AppButton } from 'components/AppButton';
import { ContentCard } from 'components/Card';
import { FloatingBlurredLayout } from 'components/layouts';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { ApiError } from 'utils/api';
import {
  CVBuilderFormValues,
  EducationEntry,
  ExperienceEntry,
  HardSkillEntry,
  LanguageSkillEntry
} from 'utils/cv-form-db';
import { datadogLogError, datadogTrackEvent } from 'utils/datadog';
import { CV_BUILDER_STEPS } from '../constants';
import { useCvBuilderData } from '../data/CvBuilderDataContext';
import { useProgressCalculation } from '../hooks/useProgressCalculation';
import { useStepNavigate } from '../hooks/useStepNavigate';
import { SectionState } from '../sectionState';
import { getStep, StepKey } from '../stepMeta';
import { ProgressBar } from './ProgressBar';
import { RootBox } from './styledComponents';
import { SummarySectionRow } from './SummarySectionRow';

/** Read-only value row. Deliberately not a bordered box: these aren't editable. */
const DetailField = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={theme => ({
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 1,
      borderBottom: `1px solid ${theme.palette.divider}`
    })}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flex: 'none' }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      color="text.primary"
      sx={{ minWidth: 0, textAlign: 'right', wordBreak: 'break-word' }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

export const Summary = () => {
  const { values } = useFormikContext<CVBuilderFormValues>();
  const stepNavigate = useStepNavigate();
  const { submit, isSubmitting } = useCvBuilderData();
  const { sections, blockingSections, completedSteps, totalSteps } =
    useProgressCalculation();
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<
    Parameters<typeof getTrans>[0]
  >('cvBuilderPage.summary.submitError');

  // Gate on the client mirror of pro-core's `CvService.assertSubmittable` instead of firing
  // a submit we already know will come back as `error.cv.incomplete`. The server keeps its
  // own gate as the authority — this can only ever fail earlier, never allow more.
  const hasBlockingGaps = blockingSections.length > 0;
  const isSubmitDisabled = isSubmitting || hasBlockingGaps;

  const sectionByKey = (key: StepKey): SectionState =>
    sections.find(section => section.key === key) ?? {
      key,
      status: 'empty',
      required: getStep(key).requiresData,
      blocking: getStep(key).requiresData
    };

  const handleEditSection = (step: string) => {
    // Navigate to edit mode - original values are restored by the data port if discarded.
    // Preserves the guest `?t=` token so a reload mid-edit can re-redeem and resume.
    stepNavigate(step, { edit: true });
  };

  const handleSubmitCV = async () => {
    try {
      await submit(values);
      datadogTrackEvent('cv_submit', { outcome: 'success' });
      setShowSuccessNotification(true);
    } catch (error) {
      // Report the failure to RUM with structured context — previously this was toast-only,
      // so genuine submit errors (server 4xx/5xx, network) were invisible in Datadog.
      const errorCode = error instanceof ApiError ? error.code : 'unknown';
      datadogLogError('CV submit failed', error, {
        errorCode,
        step: 'summary'
      });
      datadogTrackEvent('cv_submit', { outcome: 'error', errorCode });
      // The client now gates on completeness, so `error.cv.incomplete` should be
      // unreachable — kept as a backstop in case the two gates ever disagree.
      setSubmitErrorKey(
        error instanceof ApiError && error.code === 'error.cv.incomplete'
          ? 'cvBuilderPage.summary.incompleteError'
          : 'cvBuilderPage.summary.submitError'
      );
      setShowErrorNotification(true);
    }
  };

  const getInsuranceTypeLabel = (type: string | undefined) => {
    return type?.toUpperCase() === 'STATUTORY'
      ? getTrans(
          'cvBuilderPage.personalDetails.form.insuranceType.statutory.label'
        )
      : getTrans(
          'cvBuilderPage.personalDetails.form.insuranceType.private.label'
        );
  };

  const getBafoegLabel = (value: string | undefined) => {
    return value === 'true'
      ? getTrans('cvBuilderPage.selection.yes')
      : getTrans('cvBuilderPage.selection.no');
  };

  const getHoursPerWeekLabel = (hours: string | number | undefined) => {
    return `${hours || ''} ${getTrans(
      'cvBuilderPage.personalDetails.form.hoursPerWeek.label.hoursPerWeek'
    )}`;
  };

  const personalDetailsLine = () => {
    const pd = values.personalDetails;
    if (!pd) return undefined;
    return [`${pd.firstname ?? ''} ${pd.lastname ?? ''}`.trim(), pd.zip]
      .filter(Boolean)
      .join(' · ');
  };

  const renderPersonalDetails = () => {
    const pd = values.personalDetails;
    if (!pd)
      return (
        <Text variant="body2">
          {getTrans('cvBuilderPage.summary.empty.personalDetails')}
        </Text>
      );

    return (
      <Box>
        <DetailField
          label={getTrans('cvBuilderPage.personalDetails.form.firstName.label')}
          value={pd.firstname || ''}
        />
        <DetailField
          label={getTrans('cvBuilderPage.personalDetails.form.lastName.label')}
          value={pd.lastname || ''}
        />
        <DetailField
          label={getTrans('cvBuilderPage.personalDetails.form.email.label')}
          value={pd.email || ''}
        />
        <DetailField
          label={getTrans(
            'cvBuilderPage.personalDetails.form.phoneNumber.label'
          )}
          value={pd.phone || ''}
        />
        <DetailField
          label={getTrans('cvBuilderPage.personalDetails.form.zipCode.label')}
          value={pd.zip || ''}
        />
        <DetailField
          label={getTrans(
            'cvBuilderPage.personalDetails.form.insuranceType.label'
          )}
          value={getInsuranceTypeLabel(pd.insuranceType)}
        />
        <DetailField
          label={getTrans(
            'cvBuilderPage.personalDetails.form.insuranceName.label'
          )}
          value={pd.insuranceName || ''}
        />
        <DetailField
          label={getTrans(
            'cvBuilderPage.personalDetails.form.hoursPerWeek.label'
          )}
          value={getHoursPerWeekLabel(pd.weeklyAvailableHours)}
        />
        <DetailField
          label={getTrans('cvBuilderPage.personalDetails.form.bafoeg.label')}
          value={getBafoegLabel(pd.stipendBafoeg)}
        />
      </Box>
    );
  };

  const renderItemList = <T,>(
    items: T[],
    emptyMessage: string,
    renderItem: (item: T, index: number) => React.ReactNode
  ) => {
    if (!items?.length) return <Text variant="body2">{emptyMessage}</Text>;

    return (
      <Box>
        {items.map((item, index) => (
          <Box key={index}>
            {renderItem(item, index)}
            {index < items.length - 1 && (
              <Box
                sx={{
                  borderTop: '1px dashed',
                  borderColor: 'divider',
                  my: 2
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderEducation = () =>
    renderItemList(
      values.education || [],
      getTrans('cvBuilderPage.summary.empty.education'),
      (edu: EducationEntry) => (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {edu.study}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {edu.university}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {edu.studyStart} -{' '}
            {edu.currentlyEnrolled
              ? getTrans('cvBuilderPage.education.item.currentlyEnrolled.label')
              : edu.studyEnd}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getTrans('cvBuilderPage.summary.item.program')}: {edu.degree}
          </Typography>
        </>
      )
    );

  const renderProfessionalExperience = () =>
    renderItemList(
      values.professionalExperience || [],
      getTrans('cvBuilderPage.summary.empty.professionalExperience'),
      (exp: ExperienceEntry) => (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {exp.position} {getTrans('cvBuilderPage.summary.item.at')}{' '}
            {exp.companyName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {exp.startDate} -{' '}
            {exp.currentlyWorking
              ? getTrans(
                  'cvBuilderPage.professionalExperience.item.currentlyWorking.label'
                )
              : exp.endDate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {exp.description}
          </Typography>
        </>
      )
    );

  const renderHardSkills = () =>
    renderItemList(
      values.hardSkills || [],
      getTrans('cvBuilderPage.summary.empty.hardSkills'),
      (skill: HardSkillEntry) => (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {skill.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {getTrans('cvBuilderPage.summary.item.level')}: {skill.level}
          </Typography>
          {skill.professionallyUsed && (
            <Typography variant="body2" color="text.secondary">
              {getTrans(
                'cvBuilderPage.hardSkills.item.professionallyUsed.label'
              )}
            </Typography>
          )}
        </>
      )
    );

  const renderChipList = (items: string[], emptyMessage: string) => {
    if (!items?.length) return <Text variant="body2">{emptyMessage}</Text>;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, index) => (
          <Chip
            key={index}
            label={item}
            sx={theme => ({
              backgroundColor: theme.palette.primary.lighter,
              color: theme.palette.primary.dark
            })}
          />
        ))}
      </Box>
    );
  };

  const renderLanguageSkills = () =>
    renderItemList(
      values.languageSkills || [],
      getTrans('cvBuilderPage.summary.empty.languageSkills'),
      (skill: LanguageSkillEntry) => (
        <>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {skill.language}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getTrans('cvBuilderPage.summary.item.level')}: {skill.level}
          </Typography>
        </>
      )
    );

  return (
    // Reserve space for the docked floating submit bar so the last section card
    // isn't hidden behind it (mirrors the week-view content's bottom clearance).
    <RootBox sx={{ pb: 13 }}>
      <ContentCard
        title={getTrans('cvBuilderPage.overview.progress.title')}
        titleVariant="h4"
      >
        {/* Progress is derived from the CV data now, so this is honest for a returning
            talent too — which is why the old `hasSubmittedCv` copy-swap is gone. */}
        <ProgressBar showPercentage={true} showSteps={false} size="large" />
        <Text variant="caption" sx={{ display: 'block', mt: 1 }}>
          {getTransWithParams('cvBuilderPage.summary.sectionsDone', {
            current: completedSteps,
            total: totalSteps
          })}
        </Text>
      </ContentCard>

      {/* What the talent must fix, before they can discover it by tapping a dead button. */}
      {hasBlockingGaps && (
        <Alert
          severity="warning"
          data-testid="summary-blocking-gaps"
          sx={{ mb: 2 }}
        >
          <Text variant="subtitle2" color="inherit">
            {getTrans('cvBuilderPage.summary.blocking.title')}
          </Text>
          {blockingSections.map(section => (
            <Box
              key={section.key}
              component="button"
              type="button"
              onClick={() => handleEditSection(CV_BUILDER_STEPS[section.key])}
              data-dd-action-name={`cv-summary-fix:${section.key}`}
              sx={{
                display: 'block',
                border: 0,
                background: 'none',
                p: 0,
                mt: 0.5,
                font: 'inherit',
                color: 'inherit',
                textAlign: 'left',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              {getTrans(getStep(section.key).labelKey)} →
            </Box>
          ))}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        {getTrans('cvBuilderPage.summary.alert')}
      </Alert>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.personalDetails.title')}
        section={sectionByKey('PERSONAL_DETAILS')}
        summaryLine={personalDetailsLine()}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.PERSONAL_DETAILS)}
      >
        {renderPersonalDetails()}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.education.title')}
        section={sectionByKey('EDUCATION')}
        summaryLine={values.education?.[0]?.study}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.EDUCATION)}
      >
        {renderEducation()}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.professionalExperience.title')}
        section={sectionByKey('PROFESSIONAL_EXPERIENCE')}
        summaryLine={values.professionalExperience?.[0]?.position}
        onEdit={() =>
          handleEditSection(CV_BUILDER_STEPS.PROFESSIONAL_EXPERIENCE)
        }
      >
        {renderProfessionalExperience()}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.hardSkills.title')}
        section={sectionByKey('HARD_SKILLS')}
        summaryLine={values.hardSkills
          ?.map(skill => skill.name)
          .filter(Boolean)
          .join(', ')}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.HARD_SKILLS)}
      >
        {renderHardSkills()}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.softSkills.title')}
        section={sectionByKey('SOFT_SKILLS')}
        summaryLine={values.softSkills?.join(', ')}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.SOFT_SKILLS)}
      >
        {renderChipList(
          values.softSkills || [],
          getTrans('cvBuilderPage.summary.empty.softSkills')
        )}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.languageSkills.title')}
        section={sectionByKey('LANGUAGE_SKILLS')}
        summaryLine={values.languageSkills
          ?.map(skill => skill.language)
          .filter(Boolean)
          .join(', ')}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.LANGUAGE_SKILLS)}
      >
        {renderLanguageSkills()}
      </SummarySectionRow>

      <SummarySectionRow
        title={getTrans('cvBuilderPage.overview.certificates.title')}
        section={sectionByKey('CERTIFICATES')}
        summaryLine={values.certificates?.join(', ')}
        onEdit={() => handleEditSection(CV_BUILDER_STEPS.CERTIFICATES)}
      >
        {renderChipList(
          values.certificates || [],
          getTrans('cvBuilderPage.summary.empty.certificates')
        )}
      </SummarySectionRow>

      {/* Keep the submit CTA always reachable, using the same docked floating bar +
          button format as the week-view "Add shift" / "Support" buttons. */}
      <FloatingBlurredLayout>
        <Box sx={{ textAlign: 'center' }}>
          <AppButton
            color="secondary"
            sx={{ width: 160 }}
            onClick={handleSubmitCV}
            disabled={isSubmitDisabled}
            dataActionName="cv-submit"
          >
            {isSubmitting
              ? getTrans('cvBuilderPage.summary.submitting')
              : getTrans('cvBuilderPage.summary.submitButton')}
          </AppButton>
          {hasBlockingGaps && (
            <Text
              variant="caption"
              color="error.main"
              data-testid="summary-submit-blocked-reason"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {getTrans('cvBuilderPage.summary.blocking.hint')}
            </Text>
          )}
        </Box>
      </FloatingBlurredLayout>

      <Snackbar
        open={showSuccessNotification}
        autoHideDuration={4000}
        onClose={() => setShowSuccessNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessNotification(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {getTrans('cvBuilderPage.summary.submitSuccess')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorNotification}
        autoHideDuration={6000}
        onClose={() => setShowErrorNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorNotification(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {getTrans(submitErrorKey)}
        </Alert>
      </Snackbar>
    </RootBox>
  );
};
